import React, { useState, useEffect } from 'react';
import animalService from '../../../services/animalService';
import animalSalesService from '../../../services/animalSaleService';
import animalPurchasesService from '../../../services/animalPurchasesService';
import breedService from '../../../services/breedService';
import pedigreeService from '../../../services/pedigreeService';


const AnimalsTab = () => {
  const [animals, setAnimals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBreed, setFilterBreed] = useState('');
  const [filterHealth, setFilterHealth] = useState('');
  const [tradingHistory, setTradingHistory] = useState([]);
  const [tradingFilter, setTradingFilter] = useState('All');
  const [tradingLoading, setTradingLoading] = useState(false);
  const [showTradingHistory, setShowTradingHistory] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [profileAnimal, setProfileAnimal] = useState(null);
  const [breeds, setBreeds] = useState([]);
  const [pedigrees, setPedigrees] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const animalsPerPage = 10;

  const [formData, setFormData] = useState({
    name: '',
    tag_number: '',
    type: '',
    date_of_birth: '',
    weight: '',
    gender: '',
    breed: '',
    health_status: '',
    pregnancy_status: '',
    pregnancy_date: '',
    expected_delivery_date: '',
    pedigree_id: '',
    daily_production: '',
    last_checkup: '',
    notes: '',
    milk_status: '',
  });

  useEffect(() => {
    fetchAnimals();
    fetchTradingHistory();
    fetchBreeds();
    fetchPedigrees();
  }, []);

  const fetchTradingHistory = async () => {
    try {
      setTradingLoading(true);
      const [salesRes, purchasesRes] = await Promise.all([
        animalSalesService.getAllSales(),
        animalPurchasesService.getAllPurchases(),
      ]);

      const sales = (salesRes.data || []).map(s => ({ ...s, record_type: 'Sale' }));
      const purchases = (purchasesRes.data || []).map(p => ({ ...p, record_type: 'Purchase' }));

      const combined = [...sales, ...purchases].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setTradingHistory(combined);
    } catch (error) {
      console.error('Error fetching trading history:', error);
    } finally {
      setTradingLoading(false);
    }
  };

  const fetchBreeds = async () => {
    try {
      const res = await breedService.getAllBreeds();
      setBreeds(res.data || []);
    } catch (error) {
      console.error('Error fetching breeds:', error);
    }
  };

  const fetchPedigrees = async () => {
    try {
      const res = await pedigreeService.getAllPedigrees();
      setPedigrees(res.data || []);
    } catch (error) {
      console.error('Error fetching pedigrees:', error);
    }
  };

  const fetchAnimals = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (filterBreed) filters.breed = filterBreed;
      if (filterHealth) filters.health_status = filterHealth;

      const response = await animalService.getAllAnimals(filters);
      setAnimals(response.data || []);
    } catch (error) {
      console.error('Error fetching animals:', error);
      setError('Failed to load animals. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnimal = async (e) => {
    e.preventDefault();
    try {
      const animalData = { name: formData.name, tag_number: formData.tag_number };
      if (formData.type) animalData.type = formData.type;
      if (formData.date_of_birth) animalData.date_of_birth = formData.date_of_birth;
      if (formData.weight) animalData.weight = parseFloat(formData.weight);
      if (formData.gender) animalData.gender = formData.gender;
      if (formData.breed) animalData.breed = formData.breed;
      if (formData.health_status) animalData.health_status = formData.health_status;
      if (formData.pregnancy_status) animalData.pregnancy_status = formData.pregnancy_status;
      if (formData.pregnancy_status === 'Yes') {
        animalData.pregnancy_date = formData.pregnancy_date || null;
        animalData.expected_delivery_date = formData.expected_delivery_date || null;
        animalData.pedigree_id = formData.pedigree_id ? parseInt(formData.pedigree_id) : null;
      }
      if (formData.daily_production) animalData.daily_production = parseFloat(formData.daily_production);
      if (formData.last_checkup) animalData.last_checkup = formData.last_checkup;
      if (formData.notes) animalData.notes = formData.notes;
      if (formData.milk_status) animalData.milk_status = formData.milk_status;

      await animalService.createAnimal(animalData);
      alert('✅ Animal added successfully!');
      setShowAddModal(false);
      resetForm();
      fetchAnimals();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to add animal'));
    }
  };

  const handleEditAnimal = async (e) => {
    e.preventDefault();
    try {
      const animalData = { name: formData.name, tag_number: formData.tag_number };
      if (formData.type) animalData.type = formData.type;
      if (formData.date_of_birth) animalData.date_of_birth = formData.date_of_birth;
      if (formData.weight) animalData.weight = parseFloat(formData.weight);
      if (formData.gender) animalData.gender = formData.gender;
      if (formData.breed) animalData.breed = formData.breed;
      if (formData.health_status) animalData.health_status = formData.health_status;
      if (formData.pregnancy_status) animalData.pregnancy_status = formData.pregnancy_status;
      if (formData.daily_production) animalData.daily_production = parseFloat(formData.daily_production);
      if (formData.last_checkup) animalData.last_checkup = formData.last_checkup;
      if (formData.notes) animalData.notes = formData.notes;
      animalData.milk_status = formData.milk_status || 'milk';
      animalData.pregnancy_status = formData.pregnancy_status || null;
      animalData.pregnancy_date = formData.pregnancy_status === 'Yes' ? (formData.pregnancy_date || null) : null;
      animalData.expected_delivery_date = formData.pregnancy_status === 'Yes' ? (formData.expected_delivery_date || null) : null;
      animalData.pedigree_id = formData.pregnancy_status === 'Yes' && formData.pedigree_id
        ? parseInt(formData.pedigree_id)
        : null;

      await animalService.updateAnimal(selectedAnimal.id, animalData);
      alert('✅ Animal updated successfully!');
      setShowEditModal(false);
      setSelectedAnimal(null);
      resetForm();
      fetchAnimals();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to update animal'));
    }
  };

  const handleDeleteAnimal = async (animal) => {
    if (!window.confirm(`Are you sure you want to delete ${animal.name} (${animal.tag_number})?`)) return;
    try {
      await animalService.deleteAnimal(animal.id);
      alert('✅ Animal deleted successfully!');
      fetchAnimals();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to delete animal'));
    }
  };

  const openEditModal = (animal) => {
    setSelectedAnimal(animal);
    setFormData({
      name: animal.name || '',
      tag_number: animal.tag_number || '',
      type: animal.type || '',
      date_of_birth: animal.date_of_birth ? animal.date_of_birth.split('T')[0] : '',
      weight: animal.weight || '',
      gender: animal.gender || '',
      breed: animal.breed || '',
      health_status: animal.health_status || '',
      pregnancy_status: animal.pregnancy_status || '',
      pregnancy_date: animal.pregnancy_date ? animal.pregnancy_date.split('T')[0] : '',
      expected_delivery_date: animal.expected_delivery_date ? animal.expected_delivery_date.split('T')[0] : '',
      pedigree_id: animal.pedigree_id ? String(animal.pedigree_id) : '',
      daily_production: animal.daily_production || '',
      last_checkup: animal.last_checkup ? animal.last_checkup.split('T')[0] : '',
      notes: animal.notes || '',
      milk_status: animal.milk_status || 'milk',
    });
    setShowEditModal(true);
  };

  const openProfileModal = (animal) => {
    setProfileAnimal(animal);
    setShowProfileModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '', tag_number: '', type: '', date_of_birth: '',
      weight: '', gender: '', breed: '', health_status: '',
      pregnancy_status: '', pregnancy_date: '', expected_delivery_date: '',
      pedigree_id: '',
      daily_production: '', last_checkup: '', notes: '',
      milk_status: '',
    });
  };

  const handleSearch = () => { fetchAnimals(); };
  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterBreed('');
    setFilterHealth('');
    setCurrentPage(1);
    setTimeout(fetchAnimals, 100);
  };
  

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <h2 style={{ color: '#666' }}>Loading animals...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h2 style={{ color: '#e74c3c', marginBottom: '10px' }}>Error</h2>
        <p style={{ color: '#666' }}>{error}</p>
        <button onClick={fetchAnimals} style={styles.retryButton}>🔄 Retry</button>
      </div>
    );
  }

  const animalForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit} style={styles.form}>
      <div style={styles.formGrid}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Name <span style={{ color: '#e74c3c' }}>*</span></label>
          <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={styles.input} required />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Type</label>
          <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} style={styles.input}>
            <option value="">Select Type</option>
            <option value="Cow">Cow</option>
            <option value="Buffalo">Buffalo</option>
            <option value="Goat">Goat</option>
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Tag Number <span style={{ color: '#e74c3c' }}>*</span></label>
          <input type="text" value={formData.tag_number} onChange={(e) => setFormData({ ...formData, tag_number: e.target.value })} style={{ ...styles.input, background: showEditModal ? 'white' : '#f0f7ff', fontWeight: '600' }} required />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Date of Birth</label>
          <input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} style={styles.input} />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Weight (kg)</label>
          <input type="number" step="0.1" value={formData.weight} onChange={(e) => setFormData({ ...formData, weight: e.target.value })} style={styles.input} />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Gender</label>
          <select value={formData.gender} onChange={(e) => {
            const g = e.target.value;
            setFormData(prev => ({
              ...prev,
              gender: g,
              // Keep milk_status sensible per gender: a male is adult ('milk') unless already 'child';
              // a female keeps her value or defaults to 'milk'.
              milk_status: g === 'Male'
                ? (prev.milk_status === 'child' ? 'child' : 'milk')
                : (prev.milk_status || 'milk'),
            }));
          }} style={styles.input}>
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Breed</label>
          <select value={formData.breed} onChange={(e) => setFormData({ ...formData, breed: e.target.value })} style={styles.input}>
            <option value="">Select Breed</option>
            {breeds.map(b => (
              <option key={b.id} value={b.name}>{b.name}</option>
            ))}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Health Status</label>
          <select value={formData.health_status} onChange={(e) => setFormData({ ...formData, health_status: e.target.value })} style={styles.input}>
            <option value="">Select Status</option>
            <option value="Healthy">Healthy</option>
            <option value="Checkup Due">Checkup Due</option>
            <option value="Treatment">Treatment</option>
            <option value="Sick">Sick</option>
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Pregnancy Status</label>
          <select
            value={formData.pregnancy_status}
            onChange={(e) => setFormData({
              ...formData,
              pregnancy_status: e.target.value,
              pregnancy_date: e.target.value !== 'Yes' ? '' : formData.pregnancy_date,
              expected_delivery_date: e.target.value !== 'Yes' ? '' : formData.expected_delivery_date,
              pedigree_id: e.target.value !== 'Yes' ? '' : formData.pedigree_id,
            })}
            style={styles.input}
          >
            <option value="">Select Status</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
            <option value="Not Sure">Not Sure</option>
          </select>
        </div>
        {formData.pregnancy_status === 'Yes' && (
          <>
            <div style={styles.formGroup}>
              <label style={styles.label}>Pregnancy Date</label>
              <input type="date" value={formData.pregnancy_date} onChange={(e) => setFormData({ ...formData, pregnancy_date: e.target.value })} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Expected Delivery Date</label>
              <input type="date" value={formData.expected_delivery_date} onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>🐂 Pedigree (Bull)</label>
              <select
                value={formData.pedigree_id}
                onChange={(e) => setFormData({ ...formData, pedigree_id: e.target.value })}
                style={styles.input}
              >
                <option value="">Select Pedigree (optional)</option>
                {pedigrees.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.breed ? ` (${p.breed})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
        {formData.gender === 'Female' && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Milk Status</label>
            <select value={formData.milk_status} onChange={(e) => setFormData({ ...formData, milk_status: e.target.value })} style={styles.input}>
              <option value="milk">Milk</option>
              <option value="dry">Dry</option>
              <option value="child">Child</option>
            </select>
          </div>
        )}
        {formData.gender === 'Male' && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Age Group</label>
            <select value={formData.milk_status === 'child' ? 'child' : 'adult'} onChange={(e) => setFormData({ ...formData, milk_status: e.target.value === 'child' ? 'child' : 'milk' })} style={styles.input}>
              <option value="adult">Adult</option>
              <option value="child">Child</option>
            </select>
          </div>
        )}
        <div style={styles.formGroup}>
          <label style={styles.label}>Daily Production (L)</label>
          <input type="number" step="0.1" value={formData.daily_production} onChange={(e) => setFormData({ ...formData, daily_production: e.target.value })} style={styles.input} />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Last Checkup</label>
          <input type="date" value={formData.last_checkup} onChange={(e) => setFormData({ ...formData, last_checkup: e.target.value })} style={styles.input} />
        </div>
      </div>
      <div style={styles.formGroup}>
        <label style={styles.label}>Notes</label>
        <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }} rows="3" />
      </div>
      <div style={styles.modalButtons}>
        <button type="button" onClick={() => { setShowAddModal(false); setShowEditModal(false); setSelectedAnimal(null); resetForm(); }} style={styles.cancelButton}>Cancel</button>
        <button type="submit" style={styles.submitButton}>{submitLabel}</button>
      </div>
    </form>
  );
    const totalPages = Math.ceil(animals.length / animalsPerPage);
  const paginatedAnimals = animals.slice((currentPage - 1) * animalsPerPage, currentPage * animalsPerPage)

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#2c3e50', marginBottom: '8px' }}>
            {showTradingHistory ? '📋 Animal Trading History' : '🐄 Animals Management'}
          </h2>
          <p style={{ color: '#7f8c8d', fontSize: '14px' }}>
            {showTradingHistory ? `${tradingHistory.length} total transactions` : `Total Animals: ${animals.length}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          {!showTradingHistory && (
            <button onClick={() => setShowAddModal(true)} style={styles.addButton}>
              ➕ Add New Animal
            </button>
          )}
          <button onClick={() => setShowTradingHistory(!showTradingHistory)} style={styles.historyButton}>
            {showTradingHistory ? '🐄 Back to Animals' : '📋 Trading History'}
          </button>
        </div>
      </div>

      {/* Animals View */}
      {!showTradingHistory && (
        <>
          <div style={styles.filtersContainer}>
            <input
              type="text"
              placeholder="🔍 Search by name or tag..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={styles.searchInput}
            />
            <select value={filterBreed} onChange={(e) => setFilterBreed(e.target.value)} style={styles.select}>
              <option value="">All Breeds</option>
              {breeds.map(b => (
                <option key={b.id} value={b.name}>{b.name}</option>
              ))}
            </select>
            <select value={filterHealth} onChange={(e) => setFilterHealth(e.target.value)} style={styles.select}>
              <option value="">All Health Status</option>
              <option value="Healthy">Healthy</option>
              <option value="Checkup Due">Checkup Due</option>
              <option value="Treatment">Treatment</option>
              <option value="Sick">Sick</option>
            </select>
            <button onClick={handleSearch} style={styles.searchButton}>Search</button>
            <button onClick={handleClearFilters} style={styles.clearButton}>Clear</button>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={styles.th}>Tag #</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Breed</th>
                  <th style={styles.th}>Age</th>
                  <th style={styles.th}>Gender</th>
                  <th style={styles.th}>Weight (kg)</th>
                  <th style={styles.th}>Health Status</th>
                  <th style={styles.th}>Milk Status</th>
                  <th style={styles.th}>Last Checkup</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {animals.length === 0 ? (
                  <tr>
                    <td colSpan="11" style={styles.noData}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>🐄</div>
                      No animals found
                    </td>
                  </tr>
                ) : (
                  paginatedAnimals.map((animal) => (
                    <tr key={animal.id} style={styles.row}>
                      <td style={styles.td}><span style={styles.tag}>{animal.tag_number}</span></td>
                      <td style={styles.td}>
                        <strong
                          onClick={() => openProfileModal(animal)}
                          style={{ cursor: 'pointer', color: '#3498db', textDecoration: 'underline' }}
                        >
                          {animal.name}
                        </strong>
                      </td>
                      <td style={styles.td}>{animal.type || '-'}</td>
                      <td style={styles.td}>{animal.breed || '-'}</td>
                      <td style={styles.td}>
                        {animal.date_of_birth ? calculateAge(animal.date_of_birth) : animal.age ? `${parseFloat(animal.age)}y` : '-'}
                      </td>
                      <td style={styles.td}>{animal.gender || '-'}</td>
                      <td style={styles.td}>{animal.weight || '-'}</td>
                      <td style={styles.td}>
                        {animal.health_status ? (
                          <span style={getHealthStatusStyle(animal.health_status)}>{animal.health_status}</span>
                        ) : '-'}
                      </td>
                      <td style={styles.td}>
                        {animal.milk_status === 'child' ? (
                          <span style={getMilkStatusStyle('child')}>🐣 Child</span>
                        ) : animal.gender === 'Female' ? (
                          <span style={getMilkStatusStyle(animal.milk_status)}>
                            {animal.milk_status === 'milk' ? '🥛 Milk' : animal.milk_status === 'dry' ? '🌾 Dry' : '-'}
                          </span>
                        ) : '-'}
                      </td>
                      <td style={styles.td}>{animal.last_checkup ? formatDate(animal.last_checkup) : 'Never'}</td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={() => openEditModal(animal)} style={styles.editButton}>✏️ Edit</button>
                          <button onClick={() => handleDeleteAnimal(animal)} style={styles.deleteButton}>🗑️ Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '20px' }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{ padding: '8px 16px', background: currentPage === 1 ? '#e0e0e0' : '#3498db', color: currentPage === 1 ? '#95a5a6' : 'white', border: 'none', borderRadius: '8px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px' }}
              >
                ← Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button key={page} onClick={() => setCurrentPage(page)}
                  style={{ padding: '8px 14px', background: currentPage === page ? '#3498db' : 'white', color: currentPage === page ? 'white' : '#2c3e50', border: '2px solid #e0e0e0', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{ padding: '8px 16px', background: currentPage === totalPages ? '#e0e0e0' : '#3498db', color: currentPage === totalPages ? '#95a5a6' : 'white', border: 'none', borderRadius: '8px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px' }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

        
      

      {/* Trading History View */}
      {showTradingHistory && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <select
              value={tradingFilter}
              onChange={(e) => setTradingFilter(e.target.value)}
              style={{ padding: '10px 16px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
            >
              <option value="All">All Transactions</option>
              <option value="Sale">Sales Only</option>
              <option value="Purchase">Purchases Only</option>
            </select>
          </div>

          {tradingLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>⏳ Loading trading history...</div>
          ) : (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.headerRow}>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Tag / Name</th>
                    <th style={styles.th}>Breed</th>
                    <th style={styles.th}>Buyer / Seller</th>
                    <th style={styles.th}>Price (PKR)</th>
                    <th style={styles.th}>Payment Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tradingHistory.filter(r => tradingFilter === 'All' || r.record_type === tradingFilter).length === 0 ? (
                    <tr>
                      <td colSpan="7" style={styles.noData}>
                        <div style={{ fontSize: '48px', marginBottom: '10px' }}>📋</div>
                        No trading history found
                      </td>
                    </tr>
                  ) : (
                    tradingHistory
                      .filter(r => tradingFilter === 'All' || r.record_type === tradingFilter)
                      .map((record) => (
                        <tr key={`${record.record_type}-${record.id}`} style={styles.row}>
                          <td style={styles.td}>
                            <span style={{
                              background: record.record_type === 'Sale' ? '#ffeaa7' : '#d4edda',
                              color: record.record_type === 'Sale' ? '#856404' : '#155724',
                              padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '700',
                            }}>
                              {record.record_type === 'Sale' ? '🔴 Sale' : '🟢 Purchase'}
                            </span>
                          </td>
                          <td style={styles.td}>{formatDate(record.sale_date || record.purchase_date)}</td>
                          <td style={styles.td}>
                            <span style={styles.tag}>{record.tag_number}</span>
                            <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                              {record.animal_name || record.animal_type}
                            </div>
                          </td>
                          <td style={styles.td}>{record.breed || '-'}</td>
                          <td style={styles.td}>
                            <strong>{record.buyer_name || record.seller_name || '-'}</strong>
                            {(record.buyer_phone || record.seller_phone) && (
                              <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                                {record.buyer_phone || record.seller_phone}
                              </div>
                            )}
                          </td>
                          <td style={styles.td}>
                            <strong>Rs {Math.round(parseFloat(record.sale_price || record.purchase_price || 0)).toLocaleString()}</strong>
                          </td>
                          <td style={styles.td}>
                            <span style={getPaymentStatusStyle(record.payment_status)}>
                              {record.payment_status || '-'}
                            </span>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Animal Profile Modal */}
      {showProfileModal && profileAnimal && (
        <div style={styles.modalOverlay} onClick={() => setShowProfileModal(false)}>
          <div style={{ ...styles.modal, maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            {/* Profile Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
              <div>
                <div style={{ fontSize: '36px', marginBottom: '8px' }}>
                  {profileAnimal.type === 'Cow' ? '🐄' : profileAnimal.type === 'Buffalo' ? '🐃' : '🐐'}
                </div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#2c3e50', margin: '0 0 4px 0' }}>
                  {profileAnimal.name}
                </h2>
                <span style={styles.tag}>{profileAnimal.tag_number}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                {profileAnimal.health_status && (
                  <span style={getHealthStatusStyle(profileAnimal.health_status)}>{profileAnimal.health_status}</span>
                )}
                {profileAnimal.milk_status === 'child' ? (
                  <span style={getMilkStatusStyle('child')}>🐣 Child</span>
                ) : profileAnimal.gender === 'Female' && profileAnimal.milk_status && (
                  <span style={getMilkStatusStyle(profileAnimal.milk_status)}>
                    {profileAnimal.milk_status === 'milk' ? '🥛 Milk' : profileAnimal.milk_status === 'dry' ? '🌾 Dry' : '🐣 Child'}
                  </span>
                )}
              </div>
            </div>

            {/* Profile Details Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div style={profileField}>
                <div style={profileLabel}>Type</div>
                <div style={profileValue}>{profileAnimal.type || '-'}</div>
              </div>
              <div style={profileField}>
                <div style={profileLabel}>Breed</div>
                <div style={profileValue}>{profileAnimal.breed || '-'}</div>
              </div>
              <div style={profileField}>
                <div style={profileLabel}>Gender</div>
                <div style={profileValue}>{profileAnimal.gender || '-'}</div>
              </div>
              <div style={profileField}>
                <div style={profileLabel}>Age</div>
                <div style={profileValue}>
                  {profileAnimal.date_of_birth ? calculateAge(profileAnimal.date_of_birth) : profileAnimal.age ? `${parseFloat(profileAnimal.age)}y` : '-'}
                </div>
              </div>
              <div style={profileField}>
                <div style={profileLabel}>Date of Birth</div>
                <div style={profileValue}>{profileAnimal.date_of_birth ? formatDate(profileAnimal.date_of_birth) : '-'}</div>
              </div>
              <div style={profileField}>
                <div style={profileLabel}>Weight</div>
                <div style={profileValue}>{profileAnimal.weight ? `${profileAnimal.weight} kg` : '-'}</div>
              </div>
              <div style={profileField}>
                <div style={profileLabel}>Daily Production</div>
                <div style={profileValue}>{profileAnimal.daily_production ? `${profileAnimal.daily_production} L` : '-'}</div>
              </div>
              <div style={profileField}>
                <div style={profileLabel}>Last Checkup</div>
                <div style={profileValue}>{profileAnimal.last_checkup ? formatDate(profileAnimal.last_checkup) : 'Never'}</div>
              </div>
              <div style={profileField}>
                <div style={profileLabel}>Pregnancy Status</div>
                <div style={profileValue}>{profileAnimal.pregnancy_status || '-'}</div>
              </div>
              {profileAnimal.pregnancy_status === 'Yes' && profileAnimal.pedigree_id && (
                <div style={profileField}>
                  <div style={profileLabel}>Pedigree</div>
                  <div style={profileValue}>
                    {(() => {
                      const ped = pedigrees.find(p => p.id === profileAnimal.pedigree_id);
                      return ped ? `${ped.name}${ped.breed ? ` (${ped.breed})` : ''}` : '-';
                    })()}
                  </div>
                </div>
              )}
              <div style={profileField}>
                <div style={profileLabel}>Added On</div>
                <div style={profileValue}>{profileAnimal.created_at ? formatDate(profileAnimal.created_at) : '-'}</div>
              </div>
            </div>

            {/* Notes */}
            {profileAnimal.notes && (
              <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                <div style={profileLabel}>Notes</div>
                <div style={{ fontSize: '14px', color: '#2c3e50', marginTop: '6px' }}>{profileAnimal.notes}</div>
              </div>
            )}

            {/* Actions */}
            <div style={styles.modalButtons}>
              <button
                onClick={() => { setShowProfileModal(false); openEditModal(profileAnimal); }}
                style={styles.editButton}
              >
                ✏️ Edit Animal
              </button>
              <button onClick={() => setShowProfileModal(false)} style={styles.cancelButton}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Animal Modal */}
      {showAddModal && (
        <div style={styles.modalOverlay} onClick={() => { setShowAddModal(false); resetForm(); }}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Add New Animal</h2>
            {animalForm(handleAddAnimal, 'Add Animal')}
          </div>
        </div>
      )}

      {/* Edit Animal Modal */}
      {showEditModal && selectedAnimal && (
        <div style={styles.modalOverlay} onClick={() => { setShowEditModal(false); setSelectedAnimal(null); resetForm(); }}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Edit Animal - {selectedAnimal.name}</h2>
            {animalForm(handleEditAnimal, 'Update Animal')}
          </div>
        </div>
      )}
    </div>
  );
};

const profileField = {
  background: '#f8f9fa',
  borderRadius: '8px',
  padding: '12px 16px',
};

const profileLabel = {
  fontSize: '11px',
  color: '#7f8c8d',
  textTransform: 'uppercase',
  fontWeight: '600',
  marginBottom: '4px',
};

const profileValue = {
  fontSize: '15px',
  fontWeight: '600',
  color: '#2c3e50',
};

const calculateAge = (dateOfBirth) => {
  const birth = new Date(dateOfBirth);
  const today = new Date();
  const years = today.getFullYear() - birth.getFullYear();
  const months = today.getMonth() - birth.getMonth();
  if (years > 0) return `${years}y ${months >= 0 ? months : 12 + months}m`;
  return `${months >= 0 ? months : 12 + months}m`;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getHealthStatusStyle = (status) => {
  const s = {
    'Healthy': { background: '#d4edda', color: '#155724', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    'Checkup Due': { background: '#fff3cd', color: '#856404', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    'Treatment': { background: '#f8d7da', color: '#721c24', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    'Sick': { background: '#f8d7da', color: '#721c24', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  };
  return s[status] || s['Healthy'];
};

const getMilkStatusStyle = (status) => {
  const s = {
    'milk': { background: '#e3f2fd', color: '#1565c0', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    'dry': { background: '#fff3e0', color: '#e65100', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    'child': { background: '#f3e5f5', color: '#6a1b9a', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  };
  return s[status] || s['milk'];
};

const getPaymentStatusStyle = (status) => {
  const s = {
    'Paid': { background: '#d4edda', color: '#155724', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    'Partial': { background: '#fff3cd', color: '#856404', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    'Unpaid': { background: '#f8d7da', color: '#721c24', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  };
  return s[status] || s['Unpaid'];
};

const styles = {
  addButton: { padding: '10px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  historyButton: { padding: '10px 20px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  filtersContainer: { display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' },
  searchInput: { flex: '1', minWidth: '200px', padding: '12px 16px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
  select: { padding: '12px 16px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', outline: 'none', minWidth: '150px' },
  searchButton: { padding: '12px 24px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  clearButton: { padding: '12px 24px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  retryButton: { marginTop: '20px', padding: '12px 24px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  tableContainer: { background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: { padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' },
  row: { borderBottom: '1px solid #f0f0f0', transition: 'background 0.2s' },
  td: { padding: '16px', fontSize: '14px', color: '#2c3e50' },
  tag: { background: '#3498db', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '700' },
  noData: { padding: '60px 20px', textAlign: 'center', color: '#95a5a6', fontSize: '16px' },
  editButton: { padding: '6px 12px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  deleteButton: { padding: '6px 12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflow: 'auto', padding: '20px' },
  modal: { background: 'white', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '800px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflow: 'auto' },
  modalTitle: { fontSize: '22px', fontWeight: '700', marginBottom: '24px', color: '#2c3e50' },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  input: { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' },
  modalButtons: { display: 'flex', gap: '12px', marginTop: '20px' },
  cancelButton: { flex: 1, padding: '12px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitButton: { flex: 1, padding: '12px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default AnimalsTab;